import { ReactNode, useCallback, useMemo, useState } from 'react'
import { Alert, Snackbar } from '@mui/material'
import { ToastContext, ToastContextValue, ToastSeverity } from './toastContext'

interface ToastState {
  open: boolean
  message: string
  severity: ToastSeverity
}

/**
 * App-shell toast provider. One snackbar; pages call `useToast()` to fire
 * notifications without owning their own snackbar boilerplate.
 *
 * Wrap once near the root (above the router).
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'info',
  })

  const show = useCallback(
    (message: string, severity: ToastSeverity = 'info') => {
      setState({ open: true, message, severity })
    },
    []
  )

  const value = useMemo<ToastContextValue>(() => ({ show }), [show])

  const handleClose = (_e?: unknown, reason?: string) => {
    if (reason === 'clickaway') return
    setState((s) => ({ ...s, open: false }))
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={state.severity}
          onClose={() => setState((s) => ({ ...s, open: false }))}
          variant="filled"
          sx={{ maxWidth: 520 }}
        >
          {state.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  )
}
